import {
    Field,
    FieldProps,
    Form,
    Formik,
    FormikHelpers,
    FormikProps,
} from "formik";
import {useCallback, useEffect, useRef, useState} from "react";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import "./AdditionalDetailsComponent.scss";
import * as Yup from "yup";
import {FormikValues} from "formik";
import CommonService from "../../../../shared/services/common.service";
import {Grid} from "@mui/material";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import FormikRadioButtonGroupComponent
    from "../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";
import FormikDatePickerComponent from "../../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import formValidationRules from "../../../../shared/services/validation.service";
import FormikTimePickerComponent from "../../../../shared/components/core/formik-time-picker/formikTimePickerComponent";
import {AttachFile} from "@mui/icons-material";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import DialogComponent from "../../../../shared/components/dialog/dialogComponent";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import FileDragAndDropComponent from "../../../../shared/components/file-drag-n-drop/FileDragAndDropComponent";
import AttachmentViewDownloadComponent
    from "../../../../shared/components/core/attachment-view-download/attachmentViewDownloadComponent";
import {ImageConfig} from "../../../../constants";
import useAsyncState from "../../../../shared/hooks/useAsyncState";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import _ from "lodash";
import {IRootReducerState} from "../../../../store/reducers";
import {useSelector} from 'react-redux';
import moment from "moment";

interface AdditionalDetailsComponentProps {
    patientID: number;
    patientDetails: any;
    cancel: () => void;
    next: (data: any) => void;
}

const additionalDetailsValidationSchema = Yup.object({
    aliasName: Yup.string().nullable(),
    patientAccountNumber: Yup.string().nullable(),
    ssn: Yup.string().nullable(),
    nationality: Yup.string().nullable(),
    maritalStatus: Yup.string().nullable(),
    degree: Yup.string().nullable(),
    alternateId1: Yup.string().nullable(),
    alternateId2: Yup.string().nullable(),
    customField1: Yup.string().nullable(),
    customField2: Yup.string().nullable(),
    customField3: Yup.string().nullable(),
    patientId1: Yup.string().nullable(),
    patientId2: Yup.string().nullable(),
    patientId3: Yup.string().nullable(),
    assignedBy1: Yup.string().nullable(),
    assignedBy2: Yup.string().nullable(),
    assignedBy3: Yup.string().nullable(),
    isPatientDead: Yup.boolean().nullable(),
    isDrivingLicenseAvailable: Yup.boolean().nullable(),
    timeOfDeath: Yup.string().nullable(),
    dateOfDeath: Yup.string().nullable(),
    drivingLicenseNumber: Yup.string().when('isDrivingLicenseAvailable', {
        is: true,
        then: Yup.string().nullable().required('Driving License Number is required'),
        otherwise: Yup.string().nullable(),
    }),
});

interface CreatePatientAdditionalDetailsFormSchema {
    aliasName: string;
    patientAccountNumber: string;
    ssn: string;
    nationality: string;
    maritalStatus: string;
    degree: string;
    alternateId1: string;
    alternateId2: string;
    customField1: string;
    customField2: string;
    customField3: string;
    patientId1: string;
    patientId2: string;
    patientId3: string;
    assignedBy1: string;
    assignedBy2: string;
    assignedBy3: string;
    isPatientDead: boolean;
    timeOfDeath: string;
    dateOfDeath: string;
    isDrivingLicenseAvailable: boolean;
    drivingLicenseNumber: string; // new api for driving license number
}

const formInitialValues: CreatePatientAdditionalDetailsFormSchema = {
    aliasName: "",
    patientAccountNumber: "",
    ssn: "",
    nationality: "",
    maritalStatus: "",
    degree: "",
    alternateId1: "",
    alternateId2: "",
    customField1: "",
    customField2: "",
    customField3: "",
    patientId1: "",
    patientId2: "",
    patientId3: "",
    assignedBy1: "",
    assignedBy2: "",
    assignedBy3: "",
    isPatientDead: false,
    timeOfDeath: "",
    dateOfDeath: "",
    isDrivingLicenseAvailable: false,
    drivingLicenseNumber: "",
};


type Attachment = {
    id: number;
    patientId: number;
    title: string;
    secureUrl: string;
    mimeType: string;
    attachmentType: string;
    attachmentName: null;
    createdAt: string;
    updatedAt: string;
    url: string;
};

const getDrivingLicenseAttachments = (results: Attachment[]): Attachment[] => {
    return results.filter(attachment => attachment.attachmentType === 'drivingLicense');
};

const AdditionalDetailsComponent = (props: AdditionalDetailsComponentProps) => {
    const {patientID, patientDetails, cancel, next} = props;
    const {maritalStatusList} = useSelector((state: IRootReducerState) => state.staticData);

    const formikRef = useRef<FormikProps<FormikValues>>(null);
    const [formInitialState, setFormInitialState] =
        useState<CreatePatientAdditionalDetailsFormSchema>(formInitialValues);
    //submitted driving license
    const [submittedAttachments, setSubmittedAttachments] = useState<Attachment[]>([]);

    const [attachmentFile, setAttachmentFile] = useState<File[] | null>(null);
    const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
    const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);

    const handleRemoveFile = useCallback(
        (file: any) => {
            const updatedFiles = attachmentFile
                ? attachmentFile.filter((fileItem: any) => fileItem.name !== file.name)
                : [];
            setAttachmentFile(updatedFiles);
        },
        [attachmentFile]
    );

    const handleAttachmentSubmit = useCallback(
        (payload: {
            isDrivingLicenseAvailable: boolean;
            drivingLicenseNumber: string;
        }) => {
            const formData = new FormData();
            // Append the files
            attachmentFile?.forEach((file: File) => {
                formData.append("files", file);
            });
            formData.append(
                "drivingLicenseNumber",
                payload?.drivingLicenseNumber || ""
            );
            formData.append(
                "isDrivingLicenseAvailable",
                payload.isDrivingLicenseAvailable.toString()
            );
            formData.append(
                'attachmentType', "drivingLicense"
            );

            setIsAttachmentUploading(true);
            CommonService._patient
                .addPatientDrivingLicense(patientID.toString(), formData)
                .then((res: IAPIResponseType<{
                    message: string,
                    data: { results: Attachment[], patientDataUpdateResult: any }
                }>) => {

                    const drivingLicenseAttachments = getDrivingLicenseAttachments(res?.data?.results || []);
                    setSubmittedAttachments(drivingLicenseAttachments);
                    CommonService._alert.showToast(
                        res.message || "Driving license added successfully",
                        "success"
                    );
                    setIsAttachmentUploading(false);
                    setIsAttachmentDialogOpen(false);
                    setAttachmentFile(null);
                })
                .catch((error) => {
                    CommonService._alert.showToast(
                        error?.error || "Error adding driving license",
                        "error"
                    );
                    setIsAttachmentUploading(false);
                });
        },
        [patientID, attachmentFile]
    );

    const {loading: isAttachmentDeleting, execute: deleteAttachment} =
        useAsyncState();
    const removeLicenseAttachment = useCallback(
        (attachmentId: any) => {
            const payload = {
                patientId: patientID,
                attachmentId: attachmentId,
            };
            const id = CommonService._alert.loadingToast(
                "Deleting driving license attachment"
            );
            deleteAttachment(async () => {
                return CommonService._patient
                    .removePatientDrivingLicense(payload)
                    .then((response: IAPIResponseType<{ message: string, data: Attachment }>) => {

                        const deletedAttachment = response?.data as Attachment;
                        setSubmittedAttachments(submittedAttachments.filter(attachment => attachment.id !== deletedAttachment.id));
                        // setIsEdited(true);
                        setAttachmentFile((oldState: any) => {
                            const updatedList = _.cloneDeep(oldState);
                            const index = updatedList?.findIndex(
                                (item: any) => item?.id === attachmentId
                            );
                            updatedList?.splice(index, 1);
                            return updatedList;
                        });
                        CommonService._alert.updateToast(
                            id,
                            response.message || "Deleted successfully",
                            "success"
                        );
                    })
                    .catch((error: any) => {
                        CommonService._alert.updateToast(
                            id,
                            error?.message || "Error deleting attachment",
                            "error"
                        );
                    });
            });
        },
        [deleteAttachment, patientID, submittedAttachments]
    );

    const handleAttachmentRemove = useCallback(
        (id: any) => {
            CommonService.openConfirmationDialog({
                confirmationTitle: "Please confirm!",
                confirmationSubTitle:
                    "Are you sure you want to delete this attachment?",
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.DeleteImage}`,
                direction: "up",
                yes: {
                    text: "Confirm",
                    color: "primary",
                },
                no: {
                    text: "Cancel",
                    color: "primary",
                },
            })
                .then(() => {
                    removeLicenseAttachment(id);
                })
                .catch(() => {
                });
        },
        [removeLicenseAttachment]
    );

    const onFormSubmit = useCallback(
        (
            values: any,
            {setSubmitting, setErrors, validateForm}: FormikHelpers<any>
        ) => {
            const payload = {...values};
            //format the dateOfDeath to MM/DD/YYYY
            payload.dateOfDeath = payload.dateOfDeath
                ? CommonService.convertDateFormat(values["dateOfDeath"], "YYYY-MM-DD")
                : null;
            CommonService._patient
                .addPatientAdditionalDetails(patientID.toString(), payload)
                .then((res) => {
                    CommonService._alert.showToast(
                        res.message || "Additional details added successfully",
                        "success"
                    );
                    next(res.data);
                })
                .catch((error) => {
                    CommonService.handleErrors(setErrors, error);
                    CommonService._alert.showToast(
                        error.error || "Something went wrong",
                        "error"
                    );
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
        [patientID, next]
    );

    useEffect(() => {
        if (patientDetails) {
            const filteredDetails = Object.keys(formInitialValues).reduce(
                (acc: Partial<CreatePatientAdditionalDetailsFormSchema>, field) => {
                    if (patientDetails && field in patientDetails) {
                        acc[field as keyof CreatePatientAdditionalDetailsFormSchema] =
                            patientDetails[field];
                    }
                    return acc;
                },
                {}
            );
            setFormInitialState(
                filteredDetails as CreatePatientAdditionalDetailsFormSchema
            );

            //set the submitted attachments
            if (
                patientDetails?.patientAttachments &&
                patientDetails?.patientAttachments?.length > 0
            ) {
                setSubmittedAttachments(patientDetails?.patientAttachments);
            }
        }
    }, [patientDetails]);

    return (
        <div className={"additional-details-screen screen t-form-screen"}>
            <FormTitleWithNavigationBack
                title={"Additional Details"}
                tooltipData={
                    "This includes the additional information (such as alternate IDs, SSN number etc.) of the patient."
                }
            />
            <div className="t-form">
                <Formik
                    initialValues={formInitialState}
                    validationSchema={additionalDetailsValidationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validateOnMount={true}
                    enableReinitialize={true}
                    innerRef={formikRef}
                    onSubmit={onFormSubmit}
                >
                    {({
                          values,
                          errors,
                          touched,
                          handleChange,
                          handleBlur,
                          handleSubmit,
                          setFieldValue,
                          isSubmitting,
                          isValid,
                      }) => (
                        <Form className="patient-additional-details-add-holder">
                            <div className="t-form-controls-wrapper">
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"aliasName"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Alias Name"}
                                                        placeholder={"Enter Alias Name"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"patientAccountNumber"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Patient Account Number"}
                                                        placeholder={"Enter Patient Account Number"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"ssn"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Social Security Number"}
                                                        placeholder={"Enter Social Security Number"}
                                                        type={"number"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"nationality"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Nationality"}
                                                        placeholder={"Enter Nationality"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"maritalStatus"}>
                                                {(field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        label={"Marital Status"}
                                                        placeholder={"Select Marital Status"}
                                                        required={false}
                                                        formikField={field}
                                                        options={maritalStatusList}
                                                        getOptionLabel={(option: any) => option.title}
                                                        valueKey={"code"}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"degree"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Degree"}
                                                        placeholder={"Enter Degree"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"alternateId1"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Alternate ID 1"}
                                                        placeholder={"Enter Alternate ID 1"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"alternateId2"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Alternate ID 2"}
                                                        placeholder={"Enter Alternate ID 2"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"customField1"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Custom Field 1"}
                                                        placeholder={"Enter Custom Field 1"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"customField2"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Custom Field 2"}
                                                        placeholder={"Enter Custom Field 2"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"customField3"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Custom Field 3"}
                                                        placeholder={"Enter Custom Field 3"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                        </Grid>
                                    </Grid>
                                    <div className="horizontal-line"/>
                                    <div className="form-sub-title">Additional IDs</div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"patientId1"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Patient ID 1"}
                                                        placeholder={"Enter Patient ID 1"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"assignedBy1"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"ID 1 Assigned By"}
                                                        placeholder={"Enter ID 1 Assigned By"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"patientId2"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Patient ID 2"}
                                                        placeholder={"Enter Patient ID 2"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"assignedBy2"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"ID 2 Assigned By"}
                                                        placeholder={"Enter ID 2 Assigned By"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"patientId3"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"Patient ID 3"}
                                                        placeholder={"Enter Patient ID 3"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"assignedBy3"}>
                                                {(field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={"ID 3 Assigned By"}
                                                        placeholder={"Enter ID 3 Assigned By"}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <>
                                        <div className="horizontal-line"/>
                                        <div className="form-sub-title">
                                            Is driving license available?
                                        </div>
                                    </>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"isDrivingLicenseAvailable"}>
                                                {(field: FieldProps) => (
                                                    <FormikRadioButtonGroupComponent
                                                        direction={"row"}
                                                        formikField={field}
                                                        titleKey={"title"}
                                                        valueKey={"code"}
                                                        isValueBoolean={true}
                                                        options={CommonService._staticData.yesNoOptions}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>

                                    {values.isDrivingLicenseAvailable && (
                                        <>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} md={6}>
                                                    <Field name={"drivingLicenseNumber"}>
                                                        {(field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={"Driving License Number"}
                                                                placeholder={"Enter Driving License Number"}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={6}>
                                                    {submittedAttachments &&
                                                    submittedAttachments?.length > 0 ? (
                                                        <>
                                                            <div className="attachment-list-wrapper">
                                                                {submittedAttachments?.map(
                                                                    (attachment: any) => (
                                                                        <div key={CommonService.getUUID()}>
                                                                            <AttachmentViewDownloadComponent
                                                                                attachmentTitle={attachment.attachmentName}
                                                                                attachment={attachment}
                                                                                showDeleteOption={!isAttachmentDeleting}
                                                                                onClick={(id: any) =>
                                                                                    handleAttachmentRemove(id)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ButtonComponent
                                                                className="mrg-top-10"
                                                                variant={"outlined"}
                                                                fullWidth={false}
                                                                color={"secondary"}
                                                                disabled={!values?.drivingLicenseNumber}
                                                                handleClick={() => {
                                                                    setIsAttachmentDialogOpen(true);
                                                                }}
                                                                startIcon={<UploadOutlinedIcon/>}
                                                                type={"button"}
                                                            >
                                                                Upload
                                                            </ButtonComponent>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}

                                    <>
                                        <div className="horizontal-line"/>
                                        <div className="form-sub-title">Is patient dead?</div>
                                    </>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={"isPatientDead"}>
                                                {(field: FieldProps) => (
                                                    <FormikRadioButtonGroupComponent
                                                        direction={"row"}
                                                        formikField={field}
                                                        options={CommonService._staticData.yesNoOptions}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>

                                    {values.isPatientDead && (
                                        <Grid container spacing={2}>
                                            <Grid item sm={12} md={6}>
                                                <Field name={"dateOfDeath"}>
                                                    {(field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            label={"Date of Death"}
                                                            placeholder={"Select Date of Death"}
                                                            required={false}
                                                            formikField={field}
                                                            maxDate={formValidationRules.today}
                                                            onUpdate={() => {
                                                                setFieldValue("timeOfDeath", '')
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>
                                            <Grid item sm={12} md={6}>
                                                <Field name={"timeOfDeath"}>
                                                    {(field: FieldProps) => (
                                                        <FormikTimePickerComponent
                                                            label={"Time of Death"}
                                                            placeholder={"Select Time of Death"}
                                                            required={false}
                                                            formikField={field}
                                                            fullWidth
                                                            disabled={!values?.dateOfDeath}
                                                            maxTime={moment(values?.dateOfDeath).isSame(moment(), 'day') ? moment() : undefined}
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    )}
                                </>
                            </div>
                            <div className="form-action-section">
                                <ButtonComponent
                                    type="button"
                                    color={"primary"}
                                    variant={"outlined"}
                                    fullWidth={true}
                                    disabled={isSubmitting}
                                    handleClick={cancel}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!isValid}
                                    fullWidth={true}
                                    isLoading={isSubmitting}
                                >
                                    Next
                                </ButtonComponent>
                            </div>
                            <DialogComponent
                                open={isAttachmentDialogOpen}
                                maxWidth={"xs"}
                                onClose={() => {
                                    setIsAttachmentDialogOpen(false);
                                }}
                                isShowCancel={true}
                                title={"Select Attachments"}
                                className={"attachment-preview-dialog"}
                            >
                                <>
                                    <FileDragAndDropComponent
                                        multiple={true}
                                        maxFiles={1}
                                        // title={"Attachments image/pdf"}
                                        onFilesDrop={(value: any) => {
                                            setAttachmentFile(value);
                                        }}
                                        acceptedFileTypes={{
                                            "image/jpeg": [],
                                            "image/png": [],
                                        }}
                                        fileSizeLimitText={
                                            "Image file is allowed upto 5MB"
                                        }
                                    />
                                    {attachmentFile &&
                                        attachmentFile?.map((file: any) => {
                                            return (
                                                <ChipComponent
                                                    label={file?.name}
                                                    onDelete={() => {
                                                        handleRemoveFile(file);
                                                    }}
                                                    icon={<AttachFile/>}
                                                    className={"attachment-chip-item mrg-top-5"}
                                                />
                                            );
                                        })}
                                    <div className="attachment-preview-dialog-footer">
                                        <ButtonComponent
                                            variant={"outlined"}
                                            fullWidth={true}
                                            handleClick={() => {
                                                setIsAttachmentDialogOpen(false);
                                            }}
                                        >
                                            Cancel
                                        </ButtonComponent>
                                        &nbsp;
                                        <ButtonComponent
                                            variant={"contained"}
                                            fullWidth={true}
                                            isLoading={isAttachmentUploading}
                                            disabled={
                                                !Boolean(attachmentFile && attachmentFile?.length > 0)
                                            }
                                            handleClick={() =>
                                                handleAttachmentSubmit({
                                                    isDrivingLicenseAvailable:
                                                    values.isDrivingLicenseAvailable,
                                                    drivingLicenseNumber: values?.drivingLicenseNumber,
                                                })
                                            }
                                        >
                                            Confirm Upload
                                        </ButtonComponent>
                                    </div>
                                </>
                            </DialogComponent>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default AdditionalDetailsComponent;
